/**
 * offlineCache.js — Read-side IndexedDB cache for API GET responses.
 *
 * Goal: when the user loses connectivity in the middle of a session
 * (between bars of cell signal, in a basement clinic, on hospital wifi
 * that randomly drops), the app should still render the patient list,
 * the active session, and the most recent transcript instead of
 * showing a useless spinner.
 *
 * HIPAA notes:
 *   • We tag every cached row with a hash of the bearer token so a
 *     different account that logs in on the same device cannot read
 *     the previous user's PHI from cache.
 *   • Cache is cleared on logout (see clearCache()).
 *   • No PHI is ever cached in the service worker / HTTP cache —
 *     only in this app-controlled IDB store.
 *   • TTL prevents indefinite retention of stale PHI in IDB.
 */
// Kept separate from chunkQueue's `pura_offline` DB on purpose:
// avoids IDB VersionError if these two libs ever drift apart.
const DB_NAME = 'pura_cache';
const DB_VERSION = 1;
const STORE = 'api_cache';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Cheap stable hash so we don't store full tokens. Not crypto-grade —
// just enough to scope cached PHI per logged-in user.
function tokenScope(token) {
  if (!token) return 'anon';
  let h = 0;
  for (let i = 0; i < token.length; i++) {
    h = ((h << 5) - h + token.charCodeAt(i)) | 0;
  }
  return `t${(h >>> 0).toString(16)}`;
}

function makeKey(token, url) {
  return `${tokenScope(token)}::${url}`;
}

export async function readCache(token, url) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(makeKey(token, url));
      req.onsuccess = () => {
        const rec = req.result;
        if (!rec) return resolve(null);
        if (rec.expiresAt && Date.now() > rec.expiresAt) return resolve(null);
        resolve(rec);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function writeCache(token, url, data, { ttlMs = 24 * 60 * 60 * 1000 } = {}) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction(STORE, 'readwrite').objectStore(STORE).put({
        key: makeKey(token, url),
        url,
        scope: tokenScope(token),
        data,
        cachedAt: Date.now(),
        expiresAt: ttlMs ? Date.now() + ttlMs : null,
      });
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
  } catch {
    /* swallow — cache is best-effort */
  }
}

export async function clearCache() {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction(STORE, 'readwrite').objectStore(STORE).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
  } catch {
    /* swallow */
  }
}

// Clear cached rows that don't belong to the currently-logged-in user.
// Useful when a different account logs in on a shared device.
export async function clearCacheForOtherUsers(currentToken) {
  const keep = tokenScope(currentToken);
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return resolve();
        if (cursor.value?.scope !== keep) cursor.delete();
        cursor.continue();
      };
      req.onerror = () => resolve();
    });
  } catch {
    /* swallow */
  }
}
